/**
 * pinnedNetworks — per-device "★ pinned" networks for the dashboard filter.
 *
 * A tenant with many networks wants "show only the ones I care about". This is a
 * personal FILTER (a favorite/star), NOT a subscription — no notifications, no
 * shared/server state. Stored in localStorage (per-device, private), mirroring
 * ``recentlyAccessed``: pinning is a personal view preference, not a mutation of
 * the network, so it must never touch a shared server field or look like an edit.
 *
 * Best-effort: any storage error (private mode / quota) is swallowed and the
 * feature degrades to "nothing pinned" (the 'Pinned' filter simply shows empty).
 *
 * Upgrade path: if cross-device pinning is ever needed, promote the read/write
 * to a DDB-backed API behind THIS SAME module — callers (the ⋮ action + the
 * dashboard filter) don't change.
 */

const KEY = 'sf.pinnedNetworks.v1';
const MAX_ENTRIES = 500; // generous cap; a set of ids stays tiny

function _read() {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function _write(map) {
  try {
    if (typeof localStorage === 'undefined') return;
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      // Keep the most-recently-pinned (highest timestamp) if we somehow overflow.
      const trimmed = Object.entries(map)
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .slice(0, MAX_ENTRIES);
      map = Object.fromEntries(trimmed);
    }
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* swallow — feature degrades to "not pinned" */
  }
}

/** The set of pinned networkIds (as a Set for O(1) membership). */
export function getPinnedSet() {
  return new Set(Object.keys(_read()));
}

/** Is this network pinned? */
export function isPinned(networkId) {
  if (!networkId) return false;
  return Object.prototype.hasOwnProperty.call(_read(), networkId);
}

/** Pin a network (stamps a timestamp so overflow-trim keeps the freshest). */
export function pinNetwork(networkId, ts = 0) {
  if (!networkId) return;
  const map = _read();
  map[networkId] = ts || 1;
  _write(map);
}

/** Unpin a network. */
export function unpinNetwork(networkId) {
  if (!networkId) return;
  const map = _read();
  if (Object.prototype.hasOwnProperty.call(map, networkId)) {
    delete map[networkId];
    _write(map);
  }
}

/** Toggle; returns the new pinned state (true = now pinned). */
export function togglePinned(networkId, ts = 0) {
  if (!networkId) return false;
  if (isPinned(networkId)) {
    unpinNetwork(networkId);
    return false;
  }
  pinNetwork(networkId, ts);
  return true;
}
